/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { observable, action, computed } from 'mobx'
import { injectable } from 'inversify'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { IAccountFacilityApiClient } from '../AccountFacilityApiClient/contracts'
import { ValidationResult } from '../../arc/utils/ValidationResult/ValidationResult'
import isEmpty from 'validator/lib/isEmpty'
import { IAccountFacilityGridStateManager, IAccountFacilityGridCriteria, IAccountFacilityGridResults, IAccountFacilityDetail, IUser, IUserFacilityDetailRow, IUserFacilityGridResults, IUserFacilityGridCriteria, IUserSelection, IState } from './contracts'
import isLength from 'validator/lib/isLength'
import isLuhnNumber from 'validator/lib/isLuhnNumber'
import { IStateCodeApiClient } from '../StateCodeApiClient/contracts'
import { IUserSessionMiddleware } from '../../arc/middleware/UserSessionMiddleware/contracts'
import { SettingsManager } from '../../configuration/SettingsManager'

@injectable()
export class AccountFacilityStateManager implements IAccountFacilityGridStateManager {
	@observable
	isSelectedFacilityIdNotExists: boolean

	@observable
	authorizedForFacility: boolean | void

	@observable
	isFacilityNameExists: boolean

	@observable
	isNpiExists: boolean

	@observable
	selectedAccountFacility: IAccountFacilityDetail

	@observable
	selectedUserFacility: IUserFacilityDetailRow

	@observable
	availableStates: IState[]

	@observable
	availableUser: IUser[]

	@observable
	availableCorporateUser: IUser[]

	@observable
	availableUserSelections: IUserSelection[]

	@observable
	itemsGridCriteria: IAccountFacilityGridCriteria

	@observable
	userFacilityGridCriteria: IUserFacilityGridCriteria

	@observable
	itemsGridResults: IAccountFacilityGridResults

	@observable
	userFacilityGridResults: IUserFacilityGridResults

	@injectProperty('IAccountFacilityApiClient')
	public accountFacilityApiClient!: IAccountFacilityApiClient

	@injectProperty('IStateCodeApiClient')
	public stateCodeApiClient!: IStateCodeApiClient

	@injectProperty('IUserSessionMiddleware')
	private readonly userSessionMiddleware!: IUserSessionMiddleware

	constructor() {
		this.selectedAccountFacility = this.getBlankItem()
		this.availableUser = []
		this.availableCorporateUser = []
		this.availableUserSelections = []
		this.availableStates = []
		this.selectedUserFacility = this.getBlankItemForUserFacility()
		this.userFacilityGridResults = {
			items: [],
			total: 0,
		}
		this.userFacilityGridCriteria = {
			page: 1,
			sizePerPage: 10,
			sortField: 'users',
			sortOrder: 'asc',
		}
		this.itemsGridCriteria = {
			page: 1,
			sizePerPage: 10,
			sortField: 'id',
			sortOrder: 'desc',
			searchTerm: '',
		}
		this.itemsGridResults = {
			items: [],
			total: 0,
		}

		this.isFacilityNameExists = false
		this.isNpiExists = false
		this.authorizedForFacility = false
		this.isSelectedFacilityIdNotExists = false
	}

	getBlankItemForUserFacility(): IUserFacilityDetailRow {
		return {
			id: 0,
			userSelection: {
				id: 0,
				isSelected: false,
			},
			users: {
				id: 0,
				firstName: '',
				lastName: '',
			},
			isCorporateUser: false,
		}
	}

	getBlankItem(): IAccountFacilityDetail {
		return {
			id: 0,
			customerEntityId: 0,
			customerEntityFacilityStatusId: 1,
			practiceLocationStateCodeId: 1,
			facilityName: '',
			npi: '',
			primaryPin: '',
			federalTaxId: '',
			active: true,
			deleted: false,
			facilityUserDetailRow: [],
			customerEntityFacilityUsers: [],
		}
	}

	@computed get validateSelectedAccountFacility(): ValidationResult {
		const result = new ValidationResult()
		const npiPrefix = '80840'
		const regex = new RegExp(SettingsManager.REACT_APP_TEXT_FIELD_VALIDATATION_REGEX().value, 'g')

		if (this.selectedAccountFacility.facilityName !== null && !isEmpty(this.selectedAccountFacility.facilityName) && !isLength(this.selectedAccountFacility.facilityName, { max: 200 })) {
			result.setMessage('facilityName', 'Facility names must be at most 200 characters long.')
		}
		if (this.selectedAccountFacility.facilityName === null || isEmpty(this.selectedAccountFacility.facilityName.trim())) {
			result.setMessage('facilityName', 'Facility Name is required.')
		}
		if (this.selectedAccountFacility.facilityName !== null && !isEmpty(this.selectedAccountFacility.facilityName) && this.selectedAccountFacility.facilityName.match(regex)) {
			result.setMessage('facilityName', 'Invalid Facility Name')
		}
		if (this.selectedAccountFacility.npi === null || isEmpty(this.selectedAccountFacility.npi.trim())) {
			result.setMessage('npi', 'NPI is required.')
		}
		if (this.selectedAccountFacility.npi !== null && !isEmpty(this.selectedAccountFacility.npi)) {
			if (!this.selectedAccountFacility.npi.match(/^[0-9]*$/)) {
				result.setMessage('npi', 'NPI must be a number.')
			}
			if (this.selectedAccountFacility.npi.length !== 10) {
				result.setMessage('npi', 'NPI value must be 10 digits.')
			}
			if (this.selectedAccountFacility.npi.substr(0, 1) !== '1' && this.selectedAccountFacility.npi.substr(0, 1) !== '2') {
				result.setMessage('npi', 'NPI should be start with either 1 or 2.')
			}
			if (!isLuhnNumber(npiPrefix + this.selectedAccountFacility.npi)) {
				result.setMessage('npi', 'Invalid NPI Value')
			}
		}
		if (this.selectedAccountFacility.primaryPin !== null && !isEmpty(this.selectedAccountFacility.primaryPin) && !isLength(this.selectedAccountFacility.primaryPin, { max: 50 })) {
			result.setMessage('primaryPin', 'Provider ID / PIN must be at most 50 characters long.')
		}
		if (this.selectedAccountFacility.federalTaxId !== null && !isEmpty(this.selectedAccountFacility.federalTaxId) && !isLength(this.selectedAccountFacility.federalTaxId, { max: 15 })) {
			result.setMessage('federalTaxId', 'Federal Tax ID must be at most 15 characters long.')
		}
		if (this.selectedAccountFacility.practiceLocationStateCodeId === 0) {
			result.setMessage('practiceLocationStateCodeId', 'Select a State.')
		}
		return result
	}

	@action loadItemsGridResults(): Promise<void> {
		return this.accountFacilityApiClient
			.list(this.itemsGridCriteria)
			.then(response => {
				this.setItemsGridResults(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when listing the account facilities')
			})
	}

	@action setAvailableStates(availableStates: IState[]): void {
		this.availableStates = []
		this.availableStates = availableStates
	}

	@action loadAvailableStates(): Promise<void> {
		return this.stateCodeApiClient
			.getAllStates()
			.then(response => this.setAvailableStates(response))
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when loading available states')
			})
	}

	@action setUserFacilityGridResults(results: IUserFacilityGridResults): void {
		this.userFacilityGridResults = results
	}

	@action setUserFacilityGridCriteria(criteria: IUserFacilityGridCriteria): Promise<void> {
		this.userFacilityGridCriteria = criteria
		return this.loadFacilityUserGridResults()
	}

	@action setItemsGridResults(results: IAccountFacilityGridResults): void {
		this.itemsGridResults = results
	}

	@action setItemsGridCriteria(criteria: IAccountFacilityGridCriteria): Promise<void> {
		this.itemsGridCriteria = criteria
		return this.loadItemsGridResults()
	}

	@action setSelectedItem(item: IAccountFacilityDetail): void {
		this.setFacilityNameStatus(false)
		this.setNpiStatus(false)
		this.selectedAccountFacility = item
	}

	@action setAccountfacilityUsers(): void {
		this.selectedAccountFacility.customerEntityFacilityUsers = []
		this.availableUserSelections.forEach(element => {
			if (element.isSelected) {
				this.selectedAccountFacility.customerEntityFacilityUsers.push({
					id: 0,
					userId: element.id,
					customerEntityFacilityId: this.selectedAccountFacility.id,
					active: true,
				})
			}
		})
	}

	@action saveOrUpdateSelectedAccountFacility(): Promise<void> {
		this.selectedAccountFacility.facilityName = this.selectedAccountFacility.facilityName.trimLeft().trimRight()
		this.selectedAccountFacility.npi = this.selectedAccountFacility.npi.trimLeft().trimRight()
		this.selectedAccountFacility.facilityUserDetailRow = []

		this.availableUser.forEach(element => {
			this.selectedAccountFacility.facilityUserDetailRow.forEach(c => {
				if (element.id === c.id) {
					c.users = element
				}
			})
		})

		this.availableUserSelections.forEach(element => {
			this.selectedAccountFacility.facilityUserDetailRow.forEach(c => {
				if (element.id === c.id) {
					c.userSelection = element
				}
			})
		})

		this.setAccountfacilityUsers()
		if (this.selectedAccountFacility.id === 0) {
			return this.accountFacilityApiClient
				.saveAccountFacility(this.selectedAccountFacility)
				.catch(error => {
					if (error.response && error.response.data && error.response.status === 409) {
						if (error.response.data.message.indexOf('Facility') !== -1) {
							this.setFacilityNameStatus(true)
						} else if (error.response.data.message.indexOf('Npi') !== -1) {
							this.setNpiStatus(true)
						}
					}
					throw new Error('An error occurred when saving the account facility')
				})
				.then(() => {
					this.itemsGridCriteria = {
						page: 1,
						sizePerPage: 10,
						sortField: 'id',
						sortOrder: 'desc',
						searchTerm: '',
					}
				})
		}
		return this.accountFacilityApiClient.updateAccountFacility(this.selectedAccountFacility).catch(error => {
			console.log(error)
			if (error.response && error.response.data && error.response.status === 409) {
				if (error.response.data.message.indexOf('Facility') !== -1) {
					this.setFacilityNameStatus(true)
				} else if (error.response.data.message.indexOf('Npi') !== -1) {
					this.setNpiStatus(true)
				}
			}
			throw new Error('An error occurred when saving the account facility')
		})
	}

	@action setCorporatefacilityUsers(): void {
		this.availableCorporateUser = []
		if (this.selectedAccountFacility.facilityUserDetailRow !== null && typeof this.selectedAccountFacility.facilityUserDetailRow !== 'undefined') {
			this.selectedAccountFacility.facilityUserDetailRow.forEach(element => {
				if (element.isCorporateUser === true) {
					this.availableCorporateUser.push(element.users)
				}
			})
		}
	}

	@action setfacilityUsers(): void {
		this.availableUserSelections = []
		if (this.selectedAccountFacility.facilityUserDetailRow !== null && typeof this.selectedAccountFacility.facilityUserDetailRow !== 'undefined') {
			this.selectedAccountFacility.facilityUserDetailRow.forEach(element => {
				this.availableUserSelections.push(element.userSelection)
			})
		}
	}

	@action loadFacilityUserGridResults(): Promise<void> {
		return this.accountFacilityApiClient
			.listUsers(this.selectedAccountFacility.id, this.userFacilityGridCriteria)
			.then(response => {
				this.setUserFacilityGridResults(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when listing the  user facilities')
			})
	}

	@action loadselectedAccountFacilityById(id: number): Promise<void> {
		this.setSelectedFacilityIdStatus(false)
		this.isFacilityNameExists = false
		this.isNpiExists = false
		this.selectedAccountFacility.id = id
		return this.accountFacilityApiClient
			.getAccountFacility(this.selectedAccountFacility.id)
			.then((response: IAccountFacilityDetail) => {
				this.selectedAccountFacility = response
				this.setfacilityUsers()
				this.setCorporatefacilityUsers()
				if (id === 0) {
					this.selectedAccountFacility.customerEntityFacilityStatusId = 1
					this.selectedAccountFacility.active = true
				}
			})
			.catch(error => {
				if (error.response && error.response.data && (error.response.status === 404 || error.response.status === 400)) {
					this.setSelectedFacilityIdStatus(true)
				}
				throw new Error('An error occurred when loading the item')
			})
	}

	@action setUserSelectionSelectedValueFor(user: IUser, value: boolean): void {
		const userSelection = this.availableUserSelections.find(x => x.id === user.id)

		if (userSelection !== null && typeof userSelection !== undefined && userSelection) {
			userSelection.isSelected = value
		}
	}

	@action setFacilityNameStatus(isExists: boolean): void {
		this.isFacilityNameExists = isExists
	}

	@action setNpiStatus(isExists: boolean): void {
		this.isNpiExists = isExists
	}

	@action setSelectedFacilityIdStatus(isNotExists: boolean): void {
		this.isSelectedFacilityIdNotExists = isNotExists
	}

	@action setAuthorizedForFacility(isHavingAccess: boolean | void): void {
		this.authorizedForFacility = isHavingAccess
	}

	@action getAuthorizationStatus(): Promise<boolean> {
		const facilityConfig = localStorage.getItem('Permission_' + SettingsManager.PRODUCT_PERMISSION_FOR_FACILITY_CONFIGURATION)
		if (facilityConfig !== null && typeof facilityConfig !== 'undefined') {
			if (facilityConfig === 'true') {
				this.setAuthorizedForFacility(true)
				return new Promise<boolean>((resolve: any) => {
					resolve(true)
				})
			}
		}
		this.setAuthorizedForFacility(false)
		return new Promise<boolean>((resolve: any) => {
			resolve(false)
		})
	}
}
